<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';

import ContactAPI from 'dashboard/api/contacts';
import BulkActionsAPI from 'dashboard/api/bulkActions';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  label: { type: String, required: true },
});

const emit = defineEmits(['added']);
const { t } = useI18n();

const dialogRef = ref(null);
const contacts = ref([]);
const searchQuery = ref('');
const currentPage = ref(1);
const totalContacts = ref(0);
const selectedContactIds = ref([]);
const isFetching = ref(false);
const isSubmitting = ref(false);

const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalContacts.value / 15))
);
const selectedCount = computed(() => selectedContactIds.value.length);
const selectedContactIdSet = computed(() => new Set(selectedContactIds.value));
const visibleContactIds = computed(() => contacts.value.map(({ id }) => id));
const allVisibleSelected = computed(
  () =>
    visibleContactIds.value.length > 0 &&
    visibleContactIds.value.every(id => selectedContactIdSet.value.has(id))
);
const someVisibleSelected = computed(
  () =>
    !allVisibleSelected.value &&
    visibleContactIds.value.some(id => selectedContactIdSet.value.has(id))
);

const fetchContacts = async (page = 1) => {
  isFetching.value = true;
  try {
    const response = searchQuery.value.trim()
      ? await ContactAPI.search(
          encodeURIComponent(searchQuery.value.trim()),
          page,
          'name',
          '',
          props.label
        )
      : await ContactAPI.get(page, 'name', '', props.label);

    contacts.value = response.data.payload;
    totalContacts.value = response.data.meta.count;
    currentPage.value = Number(response.data.meta.current_page);
  } catch {
    contacts.value = [];
    totalContacts.value = 0;
    useAlert(t('CONTACTS_LAYOUT.LABEL_CONTACTS.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const searchContacts = debounce(() => fetchContacts(1), 300);

const handleSearch = event => {
  searchQuery.value = event.target.value;
  searchContacts();
};

const toggleContact = (contactId, selected) => {
  const ids = new Set(selectedContactIds.value);
  if (selected) {
    ids.add(contactId);
  } else {
    ids.delete(contactId);
  }
  selectedContactIds.value = [...ids];
};

const toggleVisibleContacts = selected => {
  const ids = new Set(selectedContactIds.value);
  visibleContactIds.value.forEach(id => {
    if (selected) {
      ids.add(id);
    } else {
      ids.delete(id);
    }
  });
  selectedContactIds.value = [...ids];
};

const addContacts = async () => {
  if (!selectedCount.value) return;

  isSubmitting.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContactIds.value,
      labels: { add: [props.label] },
    });
    useAlert(
      t('CONTACTS_LAYOUT.LABEL_CONTACTS.SUCCESS', {
        count: selectedCount.value,
      })
    );
    emit('added');
    dialogRef.value?.close();
  } catch {
    useAlert(t('CONTACTS_LAYOUT.LABEL_CONTACTS.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const open = () => {
  contacts.value = [];
  searchQuery.value = '';
  currentPage.value = 1;
  totalContacts.value = 0;
  selectedContactIds.value = [];
  dialogRef.value?.open();
  fetchContacts();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="$t('CONTACTS_LAYOUT.LABEL_CONTACTS.TITLE', { label })"
    :description="$t('CONTACTS_LAYOUT.LABEL_CONTACTS.DESCRIPTION')"
    :confirm-button-label="
      $t('CONTACTS_LAYOUT.LABEL_CONTACTS.CONFIRM', { count: selectedCount })
    "
    :disable-confirm-button="!selectedCount"
    :is-loading="isSubmitting"
    :blur-backdrop="false"
    @confirm="addContacts"
  >
    <div class="flex min-h-0 flex-col gap-3">
      <Input
        :model-value="searchQuery"
        type="search"
        :placeholder="$t('CONTACTS_LAYOUT.LABEL_CONTACTS.SEARCH')"
        @input="handleSearch"
      />

      <div
        class="flex items-center justify-between border-b border-n-weak px-1 pb-3"
      >
        <label
          class="flex cursor-pointer items-center gap-3 text-sm text-n-slate-12"
        >
          <Checkbox
            :model-value="allVisibleSelected"
            :indeterminate="someVisibleSelected"
            :disabled="!contacts.length"
            @update:model-value="toggleVisibleContacts"
          />
          {{ $t('CONTACTS_LAYOUT.LABEL_CONTACTS.SELECT_PAGE') }}
        </label>
        <span class="text-xs text-n-slate-11">
          {{
            $t('CONTACTS_LAYOUT.LABEL_CONTACTS.SELECTED', {
              count: selectedCount,
            })
          }}
        </span>
      </div>

      <div
        v-if="isFetching"
        class="flex h-72 items-center justify-center text-n-slate-11"
      >
        <Spinner />
      </div>
      <div
        v-else-if="!contacts.length"
        class="flex h-72 flex-col items-center justify-center gap-2 text-center"
      >
        <span class="i-lucide-users size-7 text-n-slate-10" />
        <p class="text-sm text-n-slate-11">
          {{ $t('CONTACTS_LAYOUT.LABEL_CONTACTS.EMPTY') }}
        </p>
      </div>
      <div v-else class="max-h-72 overflow-y-auto pr-1">
        <label
          v-for="contact in contacts"
          :key="contact.id"
          class="flex cursor-pointer items-center gap-3 border-b border-n-weak px-2 py-3 last:border-b-0 hover:bg-n-alpha-2"
        >
          <Checkbox
            :model-value="selectedContactIdSet.has(contact.id)"
            @update:model-value="toggleContact(contact.id, $event)"
          />
          <Avatar
            :name="contact.name || contact.phone_number || contact.email || ''"
            :src="contact.thumbnail"
            :size="36"
            rounded-full
          />
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-medium text-n-slate-12">
              {{
                contact.name ||
                contact.phone_number ||
                contact.email ||
                $t('CONTACTS_LAYOUT.LABEL_CONTACTS.UNNAMED')
              }}
            </span>
            <span class="block truncate text-xs text-n-slate-11">
              {{ contact.phone_number || contact.email || '' }}
            </span>
          </span>
        </label>
      </div>

      <div
        v-if="totalContacts > 0"
        class="flex items-center justify-between border-t border-n-weak pt-3"
      >
        <span class="text-xs text-n-slate-11">
          {{
            $t('CONTACTS_LAYOUT.LABEL_CONTACTS.TOTAL', {
              count: totalContacts,
            })
          }}
        </span>
        <div class="flex items-center gap-2">
          <Button
            type="button"
            icon="i-lucide-chevron-left"
            color="slate"
            variant="ghost"
            size="sm"
            :disabled="currentPage <= 1 || isFetching"
            @click="fetchContacts(currentPage - 1)"
          />
          <span class="min-w-16 text-center text-xs text-n-slate-11">
            {{
              $t('CONTACTS_LAYOUT.LABEL_CONTACTS.PAGE', {
                current: currentPage,
                total: totalPages,
              })
            }}
          </span>
          <Button
            type="button"
            icon="i-lucide-chevron-right"
            color="slate"
            variant="ghost"
            size="sm"
            :disabled="currentPage >= totalPages || isFetching"
            @click="fetchContacts(currentPage + 1)"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
